import React from 'react';
import { Link } from 'react-router-dom';
import { useDemoContext } from '../../contexts/DemoContext';
import { formatDisplayDate } from '../../demo/demoUtils';
import {
  FileCheck,
  AlertTriangle,
  XCircle,
  Users,
  ArrowRight
} from 'lucide-react';

export default function DemoDashboardPage() {
  const { stats, policies } = useDemoContext();

  // Get upcoming expirations (sorted by days until expiration)
  const upcomingExpirations = policies
    .filter((policy) => policy.is_expiring_soon)
    .sort((a, b) => a.days_until_expiration - b.days_until_expiration)
    .slice(0, 5);

  const statCards = [
    {
      title: 'Active Policies',
      value: stats.active_policies,
      icon: FileCheck,
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600'
    },
    {
      title: 'Expiring Soon',
      value: stats.expiring_soon,
      icon: AlertTriangle,
      bgColor: 'bg-yellow-100',
      iconColor: 'text-yellow-600'
    },
    {
      title: 'Expired',
      value: stats.expired,
      icon: XCircle,
      bgColor: 'bg-red-100',
      iconColor: 'text-red-600'
    },
    {
      title: 'Active Vendors',
      value: `${stats.active_vendors}/${stats.vendor_limit}`,
      icon: Users,
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600'
    }
  ];

  return (
    <div>
      <div className="mb-8 grid gap-6 lg:grid-cols-[1.4fr_0.9fr]">
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 p-6 text-white shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">PolicyNotify Demo</p>
          <h1 className="mt-3 text-3xl font-bold leading-tight">See how expiring COIs surface before they become a problem.</h1>
          <p className="mt-3 max-w-2xl text-sm text-slate-200">
            This demo mirrors the core workflow: build a vendor list, attach insurance policies, and watch expiration risk show up across the dashboard and notifications feed.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/demo/vendors"
              className="inline-flex items-center rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition-colors"
            >
              Start With Vendors
            </Link>
            <Link
              to="/demo/policies"
              className="inline-flex items-center rounded-lg border border-slate-400 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
            >
              Jump To Policies
            </Link>
          </div>
        </div>

        <div className="card p-6">
          <p className="text-sm font-semibold text-slate-900">Recommended Demo Path</p>
          <div className="mt-4 space-y-4">
            {[
              'Add a vendor from the Vendors page.',
              'Create a policy with an expiration date that is coming up soon.',
              'Return here to see dashboard counts change.',
              'Open Notifications to see the alert generated from that policy.',
            ].map((step, index) => (
              <div key={step} className="flex items-start gap-3">
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
                  {index + 1}
                </div>
                <p className="text-sm text-slate-600">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.title} className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`h-6 w-6 ${stat.iconColor}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <Link
          to="/demo/vendors"
          className="card p-6 hover:shadow-md transition-shadow cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Add Vendor</h3>
              <p className="text-gray-600 text-sm">Track a new vendor's insurance</p>
            </div>
            <ArrowRight className="h-6 w-6 text-gray-400 group-hover:text-primary-600 transition-colors" />
          </div>
        </Link>

        <Link
          to="/demo/policies"
          className="card p-6 hover:shadow-md transition-shadow cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Add Policy</h3>
              <p className="text-gray-600 text-sm">Add a new insurance policy</p>
            </div>
            <ArrowRight className="h-6 w-6 text-gray-400 group-hover:text-primary-600 transition-colors" />
          </div>
        </Link>

        <Link
          to="/demo/notifications"
          className="card p-6 hover:shadow-md transition-shadow cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Review Alerts</h3>
              <p className="text-gray-600 text-sm">See which policies would get surfaced to your team</p>
            </div>
            <ArrowRight className="h-6 w-6 text-gray-400 group-hover:text-primary-600 transition-colors" />
          </div>
        </Link>
      </div>

      {/* Upcoming Expirations */}
      <div className="card">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Upcoming Expirations</h2>
          <Link
            to="/demo/policies?status=expiring"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            View all
          </Link>
        </div>
        <div className="divide-y">
          {upcomingExpirations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileCheck className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No upcoming expirations</p>
            </div>
          ) : (
            upcomingExpirations.map((policy) => {
              const isExpired = policy.status === 'expired';
              const daysText = isExpired
                ? `Expired ${Math.abs(policy.days_until_expiration)} days ago`
                : `${policy.days_until_expiration} days left`;

              return (
                <Link
                  key={policy.id}
                  to={`/demo/policies/${policy.id}`}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{policy.vendor_name}</h3>
                    <p className="text-sm text-gray-500">{policy.insurance_type?.name}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className={`text-sm font-medium ${
                      isExpired ? 'text-red-600' : policy.days_until_expiration <= 7 ? 'text-red-600' : 'text-yellow-600'
                    }`}>
                      {daysText}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDisplayDate(policy.expiration_date)}
                    </p>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50 p-5">
        <p className="text-sm text-blue-900">
          <strong>What this demonstrates:</strong> the same policy data drives dashboard counts, expiring filters, detail-page warnings, and the notifications inbox. The public demo keeps everything in memory so you can explore safely.
        </p>
      </div>
    </div>
  );
}
